import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, NavLink, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaLinkedin, FaGithub, FaAngleDown, FaArrowRight, FaSearch, FaCloud, FaRocket, FaLaptopCode, FaNetworkWired, FaLightbulb, FaMagic, FaBolt, FaGlobe, FaShieldAlt, FaChartLine, FaDatabase, FaCogs, FaCode, FaSync, FaTools, FaMoneyBillWave } from 'react-icons/fa';
import { FaX } from 'react-icons/fa6';
import { 
  LuBrainCircuit, LuLightbulb, LuSettings, LuBot, LuActivity, LuMap, LuPiggyBank, LuScale, LuUsers, LuMessageSquare, LuTrendingUp, LuFileText, LuUserCog, LuEye, LuGauge, LuRefreshCw, LuDollarSign, LuShieldCheck, LuCircleArrowUp, LuNetwork, LuGitMerge, LuPlug, LuUserCheck, LuGlobe as LuGlobeIcon, LuBuilding2, LuRocket 
} from 'react-icons/lu';
import { motion, AnimatePresence } from 'framer-motion';
import contentData from '../../data/contentData.json';
import Logo from '../ui/Logo';
import { pageTransition } from '../../utils/animations';
import SupportChat from '../ui/SupportChat';

const iconMap = {
  // AI Categories
  'ai-strategy': <LuBrainCircuit />,
  'ai-solution-dev': <LuLightbulb />,
  'ai-managed-services': <LuSettings />,
  'agentic-ai': <LuBot />,

  // AI Services
  'ai-readiness': <LuActivity />,
  'enterprise-ai-roadmap': <LuMap />,
  'roi-modelling': <LuPiggyBank />,
  'ethical-ai': <LuScale />,
  'executive-workshops': <LuUsers />,
  'intelligent-chatbots': <LuMessageSquare />,
  'predictive-analytics': <LuTrendingUp />,
  'document-intelligence': <LuFileText />,
  'personalization-engines': <LuUserCog />,
  'computer-vision-nlp': <LuEye />,
  'monitoring-tuning': <LuGauge />,
  'model-lifecycle': <LuRefreshCw />,
  'cost-optimization': <LuDollarSign />,
  'security-compliance': <LuShieldCheck />,
  'continuous-improvement': <LuCircleArrowUp />,
  'multi-agent-architectures': <LuNetwork />,
  'autonomous-workflow': <LuGitMerge />,
  'enterprise-integration': <LuPlug />,
  'human-in-the-loop': <LuUserCheck />,
  'scalable-ecosystems': <LuGlobeIcon />,

  // Software Development Services
  'custom-software-development': <FaLaptopCode />,
  'software-product-development': <FaRocket />,
  'saas-development': <FaCloud />,
  'software-integration-services': <FaNetworkWired />,
  'mvp-development': <FaLightbulb />,
  'digital-transformation': <FaMagic />,
  'no-code-development': <FaBolt />,
  'software-development-outsourcing': <FaGlobe />,

  // Cloud/DevOps Categories
  'aws-cloud-services': <FaCloud />,
  'multi-cloud': <FaGlobe />,
  'devops': <FaCogs />,

  // Cloud/DevOps Sub-Services
  'cloud-migration': <FaCloud />,
  'cloud-security': <FaShieldAlt />,
  'cost-management': <FaMoneyBillWave />,
  'data-analytics': <FaDatabase />,
  'generative-ai': <FaRocket />,
  'managed-services': <FaTools />,
  'hybrid-cloud': <FaNetworkWired />,
  'cloud-optimization': <FaBolt />,
  'multi-cost-management': <FaChartLine />,
  'cloud-devops': <FaCogs />,
  'iac': <FaCode />,
  'cicd': <FaSync />,

  // Cloud/DevOps New Categories
  'ai-ml': <LuBrainCircuit />,
  'case-studies': <LuFileText />,

  // AI & ML Sub-services
  'aws-ai': <LuBrainCircuit />,

  // Case Studies Services
  'enterprise-cloud-migration': <LuBuilding2 />,
  'devops-implementation': <LuSettings />,
  'cloud-security-overhaul': <LuShieldCheck />,
  'explore-all-case-studies': <LuRocket />
};

const getIcon = (id, fallback) => {
  return iconMap[id] || fallback;
};

const Layout = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [activeServiceIndex, setActiveServiceIndex] = useState(0);
  const [aiDropdownOpen, setAiDropdownOpen] = useState(false);
  const [activeAiServiceIndex, setActiveAiServiceIndex] = useState(0);
  const [developmentDropdownOpen, setDevelopmentDropdownOpen] = useState(false);
  const [activeDevelopmentServiceIndex, setActiveDevelopmentServiceIndex] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const { pathname } = useLocation();
  const dropdownRef = useRef(null);
  const aiDropdownRef = useRef(null);
  const developmentDropdownRef = useRef(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleServicesDropdown = () => {
    setServicesDropdownOpen(!servicesDropdownOpen);
  };

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesDropdownOpen(false);
      }
      if (aiDropdownRef.current && !aiDropdownRef.current.contains(event.target)) {
        setAiDropdownOpen(false);
      }
      if (developmentDropdownRef.current && !developmentDropdownRef.current.contains(event.target)) {
        setDevelopmentDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navItems = [
    { name: 'Home', path: '/', delay: 0.1 },
    { name: 'About', path: '/about', delay: 0.2 },
    { name: 'Case Studies', path: '/case-studies', delay: 0.4, hidden: true },
    { name: 'Blog', path: '/blog', delay: 0.5, hidden: true },
    { name: 'Careers', path: '/careers', delay: 0.6 },
    { name: 'FAQ', path: '/faq', delay: 0.7, hidden: true },
    { name: 'Contact', path: '/contact', delay: 0.8, hidden: true }
  ];

  // Filter out hidden items for navigation display
  const visibleNavItems = navItems.filter(item => !item.hidden);

  return (
    <div className="flex flex-col min-h-screen">


      {/* Enhanced Header */}
      {pathname.startsWith('/development') ? (
        <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm border-b border-gray-100 h-[80px] flex items-center transition-all duration-300">
          <div className="container mx-auto px-4 md:px-6 h-full flex items-center justify-between">
            <div className="flex-shrink-0">
              <Logo variant="dark" />
            </div>
            <nav className="hidden xl:flex items-center gap-6 text-[14px] font-medium text-gray-800 tracking-wide">
              <NavLink
                to="/"
                className={({ isActive }) => `
                relative px-3 py-2 font-medium transition-all duration-300 group
                ${isActive ? 'text-blue-600' : 'text-gray-800 hover:text-blue-600'}
              `}
              >
                {({ isActive }) => (
                  <>
                    <span className="relative z-10">Home</span>
                    {isActive && (
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full mx-2"
                        layoutId="activeDevNavIndicator"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      />
                    )}
                  </>
                )}
              </NavLink>

              {/* Applied & Gen AI Dropdown */}
              <div
                className="relative h-full flex items-center"
                ref={aiDropdownRef}
                onMouseEnter={() => setAiDropdownOpen(true)}
                onMouseLeave={() => setAiDropdownOpen(false)}
              >
                <Link
                  to="/services/ai"
                  className={`
                  flex items-center gap-1 h-full px-3 transition-colors duration-300 relative group
                  ${aiDropdownOpen ? 'text-blue-600' : 'text-gray-800 hover:text-blue-600'}
                `}
                  onClick={(e) => {
                    // specific logic if needed
                  }}
                >
                  <span className="relative z-10">Applied & Gen AI</span>
                  <motion.div
                    animate={{ rotate: aiDropdownOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="relative z-10"
                  >
                    <FaAngleDown className="text-xs" />
                  </motion.div>
                  
                  {aiDropdownOpen && (
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full mx-2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2 }}
                    />
                  )}
                </Link>

                <AnimatePresence>
                  {aiDropdownOpen && (
                    <motion.div
                      className="absolute top-[20px] left-1/2 -translate-x-1/2 pt-8 w-[900px] z-[100]"
                    // initial={{ opacity: 0, y: 10 }}
                    //animate={{ opacity: 1, y: 0 }}
                    //exit={{ opacity: 0, y: 10 }}
                    //transition={{ duration: 0.2 }}
                    >
                      <div className="bg-white/95 backdrop-blur-xl shadow-2xl rounded-2xl border border-gray-100 overflow-hidden ring-1 ring-black/5">

                        {/* Gradient header */}
                        <div className="bg-gradient-to-r from-blue-50/50 to-purple-50/50 border-b border-gray-100 p-4 text-center">
                          <h3 className="text-lg font-bold text-gray-900">Applied & Gen AI Services</h3>
                        </div>

                        <div className="flex flex-col lg:flex-row h-[500px]">
                          {/* Left side - Categories */}
                          <div className="w-full lg:w-[280px] lg:border-r border-gray-100 bg-gray-50/30 overflow-y-auto custom-scrollbar">
                            <ul className="p-2 space-y-1">
                              {contentData.aiServices && contentData.aiServices.map((category, index) => (
                                <motion.li
                                  key={category.id || index}
                                  onMouseEnter={() => setActiveAiServiceIndex(index)}
                                >
                                  <Link
                                    to={`/development/${category.id}`}
                                    className={`group flex items-center justify-between p-3 rounded-lg transition-all duration-200 border ${activeAiServiceIndex === index
                                        ? 'bg-white shadow-sm border-blue-100 ring-1 ring-blue-50'
                                        : 'hover:bg-white/60 border-transparent'
                                      }`}
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-sm transition-transform duration-200 ${activeAiServiceIndex === index ? 'bg-gradient-to-r from-blue-600 to-purple-600 scale-105' : 'bg-gray-400 group-hover:bg-gray-500'
                                        }`}>
                                        <span className="text-lg">{getIcon(category.id, category.icon)}</span>
                                      </div>
                                      <span className={`font-medium transition-colors duration-200 text-sm ${activeAiServiceIndex === index ? 'text-gray-900' : 'text-gray-600 group-hover:text-gray-900'
                                        }`}>
                                        {category.category}
                                      </span>
                                    </div>
                                    {activeAiServiceIndex === index && (
                                      <FaArrowRight className="text-blue-500 text-xs" />
                                    )}
                                  </Link>
                                </motion.li>
                              ))}
                            </ul>
                          </div>

                          {/* Right side - Services */}
                          <div className="flex-1 flex flex-col bg-white">
                            <div className="flex-1 p-6 overflow-y-auto custom-scrollbar">
                              <motion.div
                                key={activeAiServiceIndex}
                                className="grid grid-cols-2 gap-4"
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                {contentData.aiServices && contentData.aiServices[activeAiServiceIndex] && contentData.aiServices[activeAiServiceIndex].services.map((service, index) => (
                                  <motion.div
                                    key={service.id || index}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.2, delay: index * 0.03 }}
                                  >
                                    <Link
                                      to={`/development/${contentData.aiServices[activeAiServiceIndex].id}`}
                                      className="group p-4 rounded-xl hover:bg-gray-50 transition-all duration-200 border border-gray-100 hover:border-blue-100 hover:shadow-sm block h-full"
                                      onClick={() => setAiDropdownOpen(false)}
                                    >
                                      <div className="flex items-start space-x-4">
                                        <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform duration-200">
                                          <span className="text-2xl">{getIcon(service.id, service.icon)}</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <h4 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200 mb-1 text-sm">
                                            {service.title}
                                          </h4>
                                          <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                                            {service.description}
                                          </p>
                                          {service.outcome && (
                                            <p className="mt-2 text-[10px] text-green-600 font-medium bg-green-50 px-2 py-1 rounded-md inline-block">
                                              Outcome: {service.outcome}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </Link>
                                  </motion.div>
                                ))}
                              </motion.div>
                            </div>


                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Software Development Dropdown */}
              <div
                className="relative h-full flex items-center"
                ref={developmentDropdownRef}
                onMouseEnter={() => setDevelopmentDropdownOpen(true)}
                onMouseLeave={() => setDevelopmentDropdownOpen(false)}
              >
                <Link
                  to="/development"
                  className={`
                  flex items-center gap-1 h-full px-3 transition-colors duration-300 relative group
                  ${developmentDropdownOpen ? 'text-blue-600' : 'text-gray-800 hover:text-blue-600'}
                `}
                >
                  <span className="relative z-10">Software Development</span>
                   <motion.div
                    animate={{ rotate: developmentDropdownOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="relative z-10"
                  >
                    <FaAngleDown className="text-xs" />
                  </motion.div>

                  {developmentDropdownOpen && (
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full mx-2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2 }}
                    />
                  )}
                </Link>

                <AnimatePresence>
                  {developmentDropdownOpen && (
                    <motion.div
                      className="absolute top-[20px] left-1/2 -translate-x-1/2 pt-8 w-[900px] z-[100]"
                    >
                      <div className="bg-white/95 backdrop-blur-xl shadow-2xl rounded-2xl border border-gray-100 overflow-hidden ring-1 ring-black/5">

                        {/* Gradient header */}
                        <div className="bg-gradient-to-r from-blue-50/50 to-purple-50/50 border-b border-gray-100 p-4 text-center">
                          <h3 className="text-lg font-bold text-gray-900">Software Development Services</h3>
                        </div>

                        <div className="flex flex-col lg:flex-row h-[500px]">
                          {/* Left side - Service Categories */}
                          <div className="w-full lg:w-[280px] lg:border-r border-gray-100 bg-gray-50/30 overflow-y-auto custom-scrollbar">
                            <ul className="p-2 space-y-1">
                              {contentData.services && contentData.services.find(cat => cat.id === 'software-development')?.services.map((service, index) => (
                                <motion.li
                                  key={service.id || index}
                                  onMouseEnter={() => setActiveDevelopmentServiceIndex(index)}
                                >
                                  <Link
                                    to={`/development/${service.id}`}
                                    className={`group flex items-center justify-between p-3 rounded-lg transition-all duration-200 border ${activeDevelopmentServiceIndex === index
                                        ? 'bg-white shadow-sm border-blue-100 ring-1 ring-blue-50'
                                        : 'hover:bg-white/60 border-transparent'
                                      }`}
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-sm transition-transform duration-200 ${activeDevelopmentServiceIndex === index ? 'bg-gradient-to-r from-blue-600 to-purple-600 scale-105' : 'bg-gray-400 group-hover:bg-gray-500'
                                        }`}>
                                        <span className="text-lg">{getIcon(service.id, service.icon)}</span>
                                      </div>
                                      <span className={`font-medium transition-colors duration-200 text-sm ${activeDevelopmentServiceIndex === index ? 'text-gray-900' : 'text-gray-600 group-hover:text-gray-900'
                                        }`}>
                                        {service.title}
                                      </span>
                                    </div>
                                    {activeDevelopmentServiceIndex === index && (
                                      <FaArrowRight className="text-blue-500 text-xs" />
                                    )}
                                  </Link>
                                </motion.li>
                              ))}
                            </ul>
                          </div>

                          {/* Right side - Service Details */}
                          <div className="flex-1 flex flex-col bg-white">
                            <div className="flex-1 p-6 overflow-y-auto custom-scrollbar">
                              <motion.div
                                key={activeDevelopmentServiceIndex}
                                className="grid grid-cols-2 gap-4"
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                {contentData.services && contentData.services.find(cat => cat.id === 'software-development')?.services[activeDevelopmentServiceIndex] && (
                                  <>
                                    <div className="col-span-2 p-4 rounded-xl bg-gray-50 border border-gray-100">
                                      <div className="flex items-start space-x-4">
                                        <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                                          <span className="text-3xl">{getIcon(contentData.services.find(cat => cat.id === 'software-development').services[activeDevelopmentServiceIndex].id, contentData.services.find(cat => cat.id === 'software-development').services[activeDevelopmentServiceIndex].icon)}</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <h4 className="font-bold text-xl text-gray-900 mb-2">
                                            {contentData.services.find(cat => cat.id === 'software-development').services[activeDevelopmentServiceIndex].title}
                                          </h4>
                                          <p className="text-sm text-gray-600 leading-relaxed">
                                            {contentData.services.find(cat => cat.id === 'software-development').services[activeDevelopmentServiceIndex].description}
                                          </p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Key Benefits */}
                                    {contentData.services.find(cat => cat.id === 'software-development').services[activeDevelopmentServiceIndex].keyBenefits && (
                                      <div className="p-4 rounded-xl border border-gray-100 hover:border-blue-100 hover:shadow-sm transition-all">
                                        <h5 className="font-semibold text-sm text-gray-900 mb-3 flex items-center gap-2">
                                          <span className="text-green-500">✓</span> Key Benefits
                                        </h5>
                                        <ul className="space-y-2">
                                          {contentData.services.find(cat => cat.id === 'software-development').services[activeDevelopmentServiceIndex].keyBenefits.map((benefit, idx) => (
                                            <li key={idx} className="flex items-start text-xs text-gray-600">
                                              <span className="text-blue-500 mr-2">•</span>
                                              <span>{benefit}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {/* Technologies */}
                                    {contentData.services.find(cat => cat.id === 'software-development').services[activeDevelopmentServiceIndex].technologiesUsed && (
                                      <div className="p-4 rounded-xl border border-gray-100 hover:border-blue-100 hover:shadow-sm transition-all">
                                        <h5 className="font-semibold text-sm text-gray-900 mb-3">Technologies Used</h5>
                                        <div className="flex flex-wrap gap-2">
                                          {contentData.services.find(cat => cat.id === 'software-development').services[activeDevelopmentServiceIndex].technologiesUsed.map((tech, idx) => (
                                            <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                                              {tech}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </motion.div>
                            </div>

                            {/* Footer Action */}
                            <div className="p-4 border-t border-gray-100 bg-gray-50/30">
                              <Link
                                to="/development"
                                className="flex items-center justify-center w-full py-3 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors bg-blue-50 rounded-lg hover:bg-blue-100"
                                onClick={() => setDevelopmentDropdownOpen(false)}
                              >
                                Explore All Development Services
                                <FaArrowRight className="ml-2 text-xs" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <NavLink
                to="/development/industries"
                className={({ isActive }) => `
                relative px-3 py-2 font-medium transition-all duration-300 group
                ${isActive ? 'text-blue-600' : 'text-gray-800 hover:text-blue-600'}
              `}
              >
                {({ isActive }) => (
                  <>
                    <span className="relative z-10">Industries</span>
                    {isActive && (
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full mx-2"
                        layoutId="activeDevNavIndicator"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            </nav>
            <div className="flex items-center gap-4">
              <Link to="/contact" className="hidden md:block px-6 py-2.5 bg-[#7B1FA2] hover:bg-purple-900 text-white rounded-full font-bold text-sm transition-all shadow-md hover:shadow-lg">
                Contact
              </Link>
              <button onClick={toggleMenu} className="xl:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                <FaBars size={24} />
              </button>
            </div>
          </div>
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                className="fixed top-[80px] left-0 w-full h-[calc(100vh-80px)] bg-white z-40 overflow-y-auto border-t border-gray-100"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="p-6 flex flex-col gap-4">
                  <Link to="/" onClick={toggleMenu} className="text-lg font-medium py-2 border-b border-gray-100">Home</Link>
                  <Link to="/services/ai" onClick={toggleMenu} className="text-lg font-medium py-2 border-b border-gray-100">Applied & Gen AI</Link>
                  <Link to="/development" onClick={toggleMenu} className="text-lg font-medium py-2 border-b border-gray-100">Software Development</Link>
                  <Link to="/contact" onClick={toggleMenu} className="mt-4 w-full text-center py-3 bg-[#7B1FA2] text-white rounded-xl font-bold">Contact Us</Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </header>
      ) : (
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled
          ? 'bg-white/90 backdrop-blur-xl shadow-xl border-b border-gray-200/50'
          : 'bg-white/95 backdrop-blur-sm shadow-lg'
          }`}>
          {/* Top notification bar */}
          <nav className="container">
            <div className="flex items-center justify-between py-4 relative">
              {/* Enhanced Logo - Positioned on left */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="flex items-center space-x-3"
              >
                <div className="relative">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl blur-lg"
                    animate={{
                      scale: [1, 1.1, 1],
                      opacity: [0.5, 0.8, 0.5]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }}
                  />
                  <Logo variant="dark" className="relative z-10" />
                </div>
              </motion.div>
              {/* Enhanced Desktop Menu - Centered */}
              <div className="hidden lg:flex items-center justify-center space-x-1">
                {/* Home */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="relative"
                >
                  <NavLink
                    to="/"
                    className={({ isActive }) => `
                    relative px-4 py-2 font-medium transition-all duration-300 group
                    ${isActive
                        ? 'text-blue-600'
                        : 'text-gray-700 hover:text-blue-600'
                      }
                  `}
                  >
                    {({ isActive }) => (
                      <>
                        <span className="relative z-10">Home</span>
                        {isActive && (
                          <motion.div
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full mx-4"
                            layoutId="activeNavIndicator"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                          />
                        )}
                      </>
                    )}
                  </NavLink>
                </motion.div>
                {/* About */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="relative"
                >
                  <NavLink
                    to="/about"
                    className={({ isActive }) => `
                    relative px-4 py-2 font-medium transition-all duration-300 group
                    ${isActive
                        ? 'text-blue-600'
                        : 'text-gray-700 hover:text-blue-600'
                      }
                  `}
                  >
                    {({ isActive }) => (
                      <>
                        <span className="relative z-10">About</span>
                        {isActive && (
                          <motion.div
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full mx-4"
                            layoutId="activeNavIndicator"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                          />
                        )}
                      </>
                    )}
                  </NavLink>
                </motion.div>

                {/* Enhanced Services Dropdown - Now 3rd position */}
                <motion.div
                  className="relative"
                  ref={dropdownRef}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  onMouseEnter={() => setServicesDropdownOpen(true)}
                  onMouseLeave={() => setServicesDropdownOpen(false)}
                >
                  <Link
                    to="/services/aws-services"
                    className={`
                    {/* Trigger Button Styles */}
                    flex items-center px-4 py-2 font-medium transition-all duration-300 group
                    ${servicesDropdownOpen
                        ? 'text-blue-600'
                        : 'text-gray-700 hover:text-blue-600'
                      }
                  `}
                    aria-expanded={servicesDropdownOpen}
                    aria-haspopup="true"
                  >
                    <span className="relative z-10">AWS Cloud Services</span>
                    <motion.div
                      animate={{ rotate: servicesDropdownOpen ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="ml-2 relative z-10"
                    >
                      <FaAngleDown />
                    </motion.div>

                    {servicesDropdownOpen && (
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full mx-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      />
                    )}
                  </Link>

                  <AnimatePresence>
                    {servicesDropdownOpen && (
                      <motion.div
                        className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[900px] z-[100]"
                      //initial={{ opacity: 0, y: -10 }}
                      //animate={{ opacity: 1, y: 0 }}
                      //exit={{ opacity: 0, y: -10 }}
                      // transition={{ duration: 0.2, ease: "easeOut" }}
                      //style={{ transformOrigin: "top center" }}
                      >
                        <div className="bg-white/95 backdrop-blur-xl shadow-2xl rounded-2xl border border-gray-100 overflow-hidden ring-1 ring-black/5">

                          {/* Gradient header */}
                          <div className="bg-gradient-to-r from-blue-50/50 to-purple-50/50 border-b border-gray-100 p-4 text-center">
                            <h3 className="text-lg font-bold text-gray-900">Our Services</h3>
                          </div>

                          <div className="flex flex-col lg:flex-row h-[500px]">
                            {/* Enhanced Left side - Main categories */}
                            <div className="w-full lg:w-[260px] lg:border-r border-gray-100 bg-gray-50/30 overflow-y-auto custom-scrollbar">
                              <ul className="p-2 space-y-1">
                                {(() => {
                                  const filteredServices = contentData.services.filter(serviceCategory => serviceCategory.id !== 'software-development');
                                  return filteredServices.map((serviceCategory, index) => (
                                    <motion.li
                                      key={serviceCategory.id || index}
                                      onMouseEnter={() => setActiveServiceIndex(index)}
                                    >
                                      <Link
                                        to={serviceCategory.link || `/services/aws-services#${serviceCategory.id}`}
                                        className={`group flex items-center justify-between p-3 rounded-lg transition-all duration-200 border ${activeServiceIndex === index
                                            ? 'bg-white shadow-sm border-blue-100 ring-1 ring-blue-50'
                                            : 'hover:bg-white/60 border-transparent'
                                          }`}
                                        onClick={(e) => {
                                          // Optional: prevent default if you want to stay in the menu only
                                          // e.preventDefault();
                                          setActiveServiceIndex(index);
                                        }}
                                      >
                                        <div className="flex items-center space-x-3">
                                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-sm transition-transform duration-200 ${activeServiceIndex === index ? 'bg-gradient-to-r from-blue-600 to-purple-600 scale-105' : 'bg-gray-400 group-hover:bg-gray-500'
                                            }`}>
                                            <span className="text-lg">{getIcon(serviceCategory.id, serviceCategory.icon)}</span>
                                          </div>
                                          <span className={`font-medium transition-colors duration-200 text-sm ${activeServiceIndex === index ? 'text-gray-900' : 'text-gray-600 group-hover:text-gray-900'
                                            }`}>
                                            {serviceCategory.category}
                                          </span>
                                        </div>
                                        {activeServiceIndex === index && (
                                          <FaArrowRight className="text-blue-500 text-xs" />
                                        )}
                                      </Link>
                                    </motion.li>
                                  ));
                                })()}
                              </ul>
                            </div>

                            {/* Enhanced Right side - Featured services */}
                            <div className="flex-1 flex flex-col bg-white">
                              <div className="flex-1 p-6 overflow-y-auto custom-scrollbar">
                                <motion.div
                                  key={activeServiceIndex}
                                  className="grid grid-cols-2 gap-4"
                                  initial={{ opacity: 0, x: 10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  {(() => {
                                    const filteredServices = contentData.services.filter(serviceCategory => serviceCategory.id !== 'software-development');
                                    return filteredServices[activeServiceIndex]?.services.map((service, index) => (
                                      <motion.div
                                        key={service.id || index}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.2, delay: index * 0.03 }}
                                      >
                                        <Link
                                          to={service.link || `/services/aws-services/${service.id}`}
                                          className="group p-4 rounded-xl hover:bg-gray-50 transition-all duration-200 border border-gray-100 hover:border-blue-100 hover:shadow-sm block h-full"
                                          onClick={() => setServicesDropdownOpen(false)}
                                        >
                                          <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform duration-200">
                                              <span className="text-2xl">{getIcon(service.id, service.icon)}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <h4 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200 mb-1 text-sm">
                                                {service.title}
                                              </h4>
                                              <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                                                {service.description}
                                              </p>
                                            </div>
                                          </div>
                                        </Link>
                                      </motion.div>
                                    ));
                                  })()}
                                </motion.div>
                              </div>

                              {/* Footer Action */}
                              <div className="p-4 border-t border-gray-100 bg-gray-50/30">
                                <Link
                                  to="/services/aws-services"
                                  className="flex items-center justify-center w-full py-3 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors bg-blue-50 rounded-lg hover:bg-blue-100"
                                  onClick={() => setServicesDropdownOpen(false)}
                                >
                                  Explore All Services
                                  <FaArrowRight className="ml-2 text-xs" />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>


                </motion.div>

                {/* Development */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.35 }}
                  className="relative"
                >
                  <NavLink
                    to="/development"
                    className={({ isActive }) => `
                    relative px-4 py-2 font-medium transition-all duration-300 group
                    ${isActive
                        ? 'text-blue-600'
                        : 'text-gray-700 hover:text-blue-600'
                      }
                  `}
                  >
                    {({ isActive }) => (
                      <>
                        <span className="relative z-10">We do build</span>
                        {isActive && (
                          <motion.div
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full mx-4"
                            layoutId="activeNavIndicator"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                          />
                        )}
                      </>
                    )}
                  </NavLink>
                </motion.div>


                {/* Startup */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="relative"
                >
                  <NavLink
                    to="/startup"
                    className={({ isActive }) => `
                    relative px-4 py-2 font-medium transition-all duration-300 group
                    ${isActive
                        ? 'text-blue-600'
                        : 'text-gray-700 hover:text-blue-600'
                      }
                  `}
                  >
                    {({ isActive }) => (
                      <>
                        <span className="relative z-10">Are you a startup</span>
                        {isActive && (
                          <motion.div
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full mx-4"
                            layoutId="activeNavIndicator"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                          />
                        )}
                      </>
                    )}
                  </NavLink>
                </motion.div>

                {/* Insights and Downloads */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.45 }}
                  className="relative"
                >
                  <NavLink
                    to="/insights"
                    className={({ isActive }) => `
                    relative px-4 py-2 font-medium transition-all duration-300 group
                    ${isActive
                        ? 'text-blue-600'
                        : 'text-gray-700 hover:text-blue-600'
                      }
                  `}
                  >
                    {({ isActive }) => (
                      <>
                        <span className="relative z-10">Insights and Downloads</span>
                        {isActive && (
                          <motion.div
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full mx-4"
                            layoutId="activeNavIndicator"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                          />
                        )}
                      </>
                    )}
                  </NavLink>
                </motion.div>

                {/* Remaining navigation items */}
                {visibleNavItems.slice(2).map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 + (index * 0.1) }}
                    className="relative"
                  >
                    <NavLink
                      to={item.path}
                      className={({ isActive }) => `
                      relative px-4 py-2 font-medium transition-all duration-300 group
                      ${isActive
                          ? 'text-blue-600'
                          : 'text-gray-700 hover:text-blue-600'
                        }
                    `}
                    >
                      {({ isActive }) => (
                        <>
                          <span className="relative z-10">{item.name}</span>
                          {isActive && (
                            <motion.div
                              className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full mx-4"
                              layoutId="activeNavIndicator"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.3 }}
                            />
                          )}
                        </>
                      )}
                    </NavLink>
                  </motion.div>
                ))}

                {/* Enhanced CTA Button */}

              </div>

              {/* Contact Button - Positioned on right */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="relative"
              >
                <Link
                  to="/contact"
                  className="hidden lg:block px-6 py-2.5 bg-[#7B1FA2] hover:bg-purple-900 text-white rounded-full font-bold text-sm transition-all shadow-md hover:shadow-lg"
                >
                  Contact
                </Link>
              </motion.div>

              {/* Enhanced Mobile Menu Button - Positioned on right */}
              <motion.button
                className="lg:hidden p-3 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                onClick={toggleMenu}
                aria-expanded={isMenuOpen}
                aria-label="Toggle navigation menu"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <AnimatePresence mode="wait">
                  {isMenuOpen ? (
                    <motion.div
                      key="close"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FaTimes className="text-xl text-gray-700" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="menu"
                      initial={{ rotate: 90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: -90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FaBars className="text-xl text-gray-700" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </div>
          </nav>

          {/* Enhanced Mobile Menu */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                className="lg:hidden fixed top-[120px] left-0 w-full h-[calc(100vh-120px)] bg-white/95 backdrop-blur-xl overflow-y-auto z-40"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Mobile menu background effects */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-purple-50/50"></div>
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(59,130,246,0.1),transparent_50%),radial-gradient(circle_at_80%_20%,rgba(139,92,246,0.1),transparent_50%)]"></div>

                <div className="container relative z-10 py-8 px-6">
                  <div className="space-y-4">
                    {/* Home */}
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                    >
                      <NavLink
                        to="/"
                        className={({ isActive }) => `
                        flex items-center justify-between p-4 rounded-2xl font-medium transition-all duration-300
                        ${isActive
                            ? 'text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg'
                            : 'text-gray-700 hover:bg-white hover:shadow-md'
                          }
                      `}
                        onClick={toggleMenu}
                      >
                        <span>Home</span>
                        <FaRocket className="text-sm" />
                      </NavLink>
                    </motion.div>

                    {/* About */}
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                    >
                      <NavLink
                        to="/about"
                        className={({ isActive }) => `
                        flex items-center justify-between p-4 rounded-2xl font-medium transition-all duration-300
                        ${isActive
                            ? 'text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg'
                            : 'text-gray-700 hover:bg-white hover:shadow-md'
                          }
                      `}
                        onClick={toggleMenu}
                      >
                        <span>About</span>
                        <FaSearch className="text-sm" />
                      </NavLink>
                    </motion.div>

                    {/* Enhanced Mobile Services Dropdown - Now 3rd position */}
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 }}
                    >
                      <button
                        className={`
                        flex items-center justify-between w-full p-4 rounded-2xl font-medium transition-all duration-300
                        ${servicesDropdownOpen
                            ? 'text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg'
                            : 'text-gray-700 hover:bg-white hover:shadow-md'
                          }
                      `}
                        onClick={toggleServicesDropdown}
                        aria-expanded={servicesDropdownOpen}
                      >
                        <span>AWS Cloud Services</span>
                        <motion.div
                          animate={{ rotate: servicesDropdownOpen ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <FaAngleDown />
                        </motion.div>
                      </button>

                      <AnimatePresence>
                        {servicesDropdownOpen && (
                          <motion.div
                            className="mt-4 space-y-3 max-h-[50vh] overflow-y-auto"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            {contentData.services
                              .filter(serviceCategory => serviceCategory.id !== 'software-development')
                              .map((serviceCategory, index) => (
                                <motion.div
                                  key={serviceCategory.id}
                                  className="bg-white/80 backdrop-blur-sm p-4 rounded-2xl shadow-lg border border-gray-200/50"
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.3, delay: index * 0.1 }}
                                >
                                  <Link
                                    to={`/services/aws-services#${serviceCategory.id}`}
                                    className="flex items-center justify-between font-semibold text-gray-800 hover:text-blue-600 transition-colors duration-200 mb-3"
                                    onClick={() => {
                                      setIsMenuOpen(false);
                                      setServicesDropdownOpen(false);
                                    }}
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center text-white">
                                        <span className="text-lg">{getIcon(serviceCategory.id, serviceCategory.icon)}</span>
                                      </div>
                                      <span>{serviceCategory.category}</span>
                                    </div>
                                    <FaAngleDown className="text-gray-400" />
                                  </Link>

                                  <div className="space-y-2">
                                    {serviceCategory.services.slice(0, 3).map((service, serviceIndex) => (
                                      <Link
                                        key={service.id || serviceIndex}
                                        to={`/services/aws-services/${service.id}`}
                                        className="block p-3 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors duration-200"
                                        onClick={() => {
                                          setIsMenuOpen(false);
                                          setServicesDropdownOpen(false);
                                        }}
                                      >
                                        <div className="flex items-start space-x-3">
                                          <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mt-0.5">
                                            <span className="text-xs">{service.icon}</span>
                                          </div>
                                          <div>
                                            <div className="font-medium text-sm text-gray-800">{service.title}</div>
                                            <div className="text-xs text-gray-600 mt-1">{service.description.substring(0, 60)}...</div>
                                          </div>
                                        </div>
                                      </Link>
                                    ))}
                                    {serviceCategory.services.length > 3 && (
                                      <Link
                                        to={`/services/aws-services#${serviceCategory.id}`}
                                        className="block text-sm text-blue-600 hover:text-blue-700 font-medium mt-2 text-center"
                                        onClick={() => {
                                          setIsMenuOpen(false);
                                          setServicesDropdownOpen(false);
                                        }}
                                      >
                                        + {serviceCategory.services.length - 3} more services
                                      </Link>
                                    )}
                                  </div>
                                </motion.div>
                              ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>

                    {/* Development */}
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.35 }}
                    >
                      <NavLink
                        to="/development"
                        className={({ isActive }) => `
                        flex items-center justify-between p-4 rounded-2xl font-medium transition-all duration-300
                        ${isActive
                            ? 'text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg'
                            : 'text-gray-700 hover:bg-white hover:shadow-md'
                          }
                      `}
                        onClick={toggleMenu}
                      >
                        <span>We do build</span>
                        <FaRocket className="text-sm" />
                      </NavLink>
                    </motion.div>

                    {/* Startup */}
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.4 }}
                    >
                      <NavLink
                        to="/startup"
                        className={({ isActive }) => `
                        flex items-center justify-between p-4 rounded-2xl font-medium transition-all duration-300
                        ${isActive
                            ? 'text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg'
                            : 'text-gray-700 hover:bg-white hover:shadow-md'
                          }
                      `}
                        onClick={toggleMenu}
                      >
                        <span>Are you a startup</span>
                        <FaRocket className="text-sm" />
                      </NavLink>
                    </motion.div>

                    {/* Remaining navigation items */}
                    {visibleNavItems.slice(2).map((item, index) => (
                      <motion.div
                        key={item.name}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.4 + (index * 0.1) }}
                      >
                        <NavLink
                          to={item.path}
                          className={({ isActive }) => `
                          flex items-center justify-between p-4 rounded-2xl font-medium transition-all duration-300
                          ${isActive
                              ? 'text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg'
                              : 'text-gray-700 hover:bg-white hover:shadow-md'
                            }
                        `}
                          onClick={toggleMenu}
                        >
                          <span>{item.name}</span>
                          {item.name === 'Case Studies' && <FaCloud className="text-sm" />}
                        </NavLink>
                      </motion.div>
                    ))}

                    {/* Enhanced Mobile CTA */}
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.8 }}
                      className="pt-4 mt-6 border-t border-gray-200/50"
                    >
                      <Link
                        to="/contact"
                        className="group flex items-center justify-center gap-3 w-full p-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                        onClick={toggleMenu}
                      >
                        <span>Get Started</span>
                        <FaArrowRight className="transform group-hover:translate-x-1 transition-transform duration-300" />
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </header>
      )}

      {/* Main Content with Page Transitions */}
      <main className="flex-grow pt-[76px]"> {/* Padding for fixed header - matches exact header height with no gap */}
        <AnimatePresence mode="wait">
          <motion.div
            key={window.location.pathname}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageTransition}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="bg-dark text-white">
        <div className="container py-12 px-6 md:px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="mb-2">
              <Link to="/" className="flex items-center mb-4">
                <img 
                  src="/assets/Dhyonlogo.png" 
                  alt="Dhyon Technologies" 
                  className="h-20 md:h-28 w-auto" 
                />
              </Link>
              <p className="text-gray-300 mb-4">
                {contentData.company.mission}
              </p>
              <div className="flex space-x-5">
                <a href="https://www.linkedin.com/company/dhyon-technologies/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors">
                  <FaLinkedin className="text-2xl" />
                </a>
                <a href="https://x.com/DhyonTech" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors">
                  <FaX className="text-2xl" />
                </a>

              </div>
            </div>

            {/* Quick Links */}
            <div className="mb-2">
              <h3 className="text-lg font-semibold mb-5">Quick Links</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/" className="text-gray-300 hover:text-white transition-colors">Home</Link>
                </li>
                <li>
                  <Link to="/about" className="text-gray-300 hover:text-white transition-colors">About Us</Link>
                </li>
                <li>
                  <Link to="/case-studies" className="text-gray-300 hover:text-white transition-colors">Case Studies</Link>
                </li>
                <li>
                  <Link to="/insights" className="text-gray-300 hover:text-white transition-colors">Insights & Downloads</Link>
                </li>
                <li>
                  <Link to="/careers" className="text-gray-300 hover:text-white transition-colors">Careers</Link>
                </li>
                <li>
                  <Link to="/faq" className="text-gray-300 hover:text-white transition-colors">FAQ</Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-300 hover:text-white transition-colors">Contact</Link>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div className="mb-2">
              <h3 className="text-lg font-semibold mb-5">Services</h3>
              <ul className="space-y-3">
                {[
                  { title: 'AWS Cloud Services', path: '/services/aws-services' },
                  { title: 'Software Development', path: '/development' },
                  { title: 'Applied & Gen AI', path: '/services/ai' },
                  { title: 'Startup Nurturing & Funding Process', path: '/startup' }
                ].map((service, index) => (
                  <li key={index}>
                    <Link to={service.path} className="text-gray-300 hover:text-white transition-colors">{service.title}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="mb-2">
              <h3 className="text-lg font-semibold mb-5">Contact Us</h3>
              <ul className="space-y-3">
                {contentData.company.locations.map((loc, idx) => (
                  <li key={idx} className="text-gray-300 flex flex-col items-start mb-3">
                    <span className="font-semibold text-white mb-1">{loc.city} Office</span>
                    <span className="inline-block text-sm leading-relaxed text-gray-400 mb-1">{loc.address}</span>
                    <span className="inline-block text-sm text-gray-300 hover:text-white transition-colors">
                      <a href={`tel:${loc.phone.replace(/\s/g, '')}`}>{loc.phone}</a>
                    </span>
                  </li>
                ))}
                <li className="text-gray-300 flex items-center">
                  <span className="inline-block">{contentData.company.contact.email}</span>
                </li>
                <li className="text-gray-300 flex items-center">
                  <span className="inline-block">info@dhyon.com</span>
                </li>
                <li className="text-gray-300 flex items-center">
                  <span className="inline-block">{contentData.company.contact.phone}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 mt-8 border-t border-gray-700 text-center sm:text-left sm:flex sm:flex-wrap sm:justify-between">
            <p className="text-gray-400">© {new Date().getFullYear()} {contentData.company.name}. All rights reserved.</p>
            <div className="mt-4 space-x-6 sm:mt-0">
              <Link to="#" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="#" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Support Chat */}
      <SupportChat />

    </div>
  );
};

export default Layout; 